import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { ReactComponent as YesIcon } from '../icons/icon-yes.svg';
import { ReactComponent as NoIcon } from '../icons/icon-no.svg';
import { ReactComponent as LoadingIcon } from '../icons/icon-loading.svg';
import { withSocket } from '../providers/SocketProvider';

const INITIAL_DELAY_BEFORE_SHOW_SAVED_MS = 500;
const MESSAGE_FADE_DELAY_MS = 1500;
const FADE_DURATION_MS = 500;

const SAVING_STATE_IN_PROGRESS = 'IN_PROGRESS';
const SAVING_STATE_SAVED = 'SAVED';
const SAVING_STATE_FADING = 'FADING';

export const SavingIndicator = ({
  lastChangedAt,
  lastSavedAt,
  socketsConnected,
}) => {
  const [lastShownSavedAt, setLastShownSavedAt] = useState(lastSavedAt);
  const [savingState, setSavingState] = useState(null);
  const [isInSaveCompletedDisplayCycle, setIsInSaveCompletedDisplayCycle] =
    useState(false);
  const isShareDBSocketConnected = socketsConnected && socketsConnected.sharedb;

  const wait = (delayMs) =>
    new Promise((resolve) => {
      setTimeout(resolve, delayMs);
    });

  const completeSave = () => {
    setLastShownSavedAt(new Date().getTime());
    setSavingState(null);
    setIsInSaveCompletedDisplayCycle(false);
  };

  // Start the saving indication if it's changed and not already in progress.
  useEffect(() => {
    const changesSinceLastShown = lastChangedAt - lastShownSavedAt > 0;
    if (
      isShareDBSocketConnected &&
      changesSinceLastShown &&
      savingState === null
    ) {
      setSavingState(SAVING_STATE_IN_PROGRESS);
    }
  }, [lastChangedAt, lastShownSavedAt, savingState, isShareDBSocketConnected]);

  // if the saving indicator is in process, check if the save actually completed and if so, start the
  // a cycle of showing the alert and then fading it out.
  useEffect(() => {
    const startSaveCompletedStateTransitions = async () => {
      await wait(INITIAL_DELAY_BEFORE_SHOW_SAVED_MS);
      setSavingState(SAVING_STATE_SAVED);
      await wait(MESSAGE_FADE_DELAY_MS);
      setSavingState(SAVING_STATE_FADING);
      await wait(FADE_DURATION_MS);
      completeSave();
    };

    if (
      savingState === SAVING_STATE_IN_PROGRESS &&
      !isInSaveCompletedDisplayCycle &&
      lastSavedAt > lastShownSavedAt
    ) {
      setIsInSaveCompletedDisplayCycle(true);
      startSaveCompletedStateTransitions();
    }
  }, [
    savingState,
    isInSaveCompletedDisplayCycle,
    lastSavedAt,
    lastShownSavedAt,
  ]);

  if (!isShareDBSocketConnected) {
    return (
      <span>
        <span className="ml6" style={{ position: 'relative', top: '1px' }}>
          <NoIcon />
        </span>
        <span className="ml1 body-xs text-black-tint-40">Error saving</span>
      </span>
    );
  }
  if (savingState) {
    return (
      <span style={{ position: 'absolute' }} className="ml3">
        {savingState === SAVING_STATE_IN_PROGRESS ? (
          <span
            className="icon-container"
            style={{ position: 'relative', top: '2px' }}
          >
            <LoadingIcon />
          </span>
        ) : (
          <span
            className={cx('fade-out', {
              hidden: savingState === SAVING_STATE_FADING,
            })}
          >
            <YesIcon />
            <span className="ml1 body-xs text-black-tint-40">
              Changes Saved
            </span>
          </span>
        )}
      </span>
    );
  }
  return null;
};

export default withSocket()(SavingIndicator);
